import { useDatabase } from '@/hooks/useDatabase';
import { Alert, Button, Card, Spinner } from 'flowbite-react';
import { jwtDecode } from "jwt-decode";
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CredentialValue, saveCredential } from '../utils/credentialStorage';
import {
    clearCodeVerifier,
    clearOAuthState,
    generateCodeChallenge,
    generateCodeVerifier,
    getCodeVerifier,
    getOAuthState,
    parseUrlParams,
    storeCodeVerifier,
    storeOAuthState
} from '../utils/oauth2';

export interface OAuth2LoginButtonProps {
    label: string;
    clientId: string;
    issuer: string;
    authorizeUrl: string;
    redirectUrl: string;
    scope: string;
    className?: string;
}

export const OAuth2LoginButton: React.FC<OAuth2LoginButtonProps> = ({
    label,
    clientId,
    issuer,
    authorizeUrl,
    redirectUrl,
    scope,
    className
}) => {
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const { workspaceId } = useDatabase();

    // TODO make this a URL so it's a normal, visible link and not an imperative action button
    const handleLogin = async () => {
        try {
            setIsLoading(true);

            // Generate PKCE code verifier and challenge
            const codeVerifier = generateCodeVerifier();
            const codeChallenge = await generateCodeChallenge(codeVerifier);

            // Generate state parameter to prevent CSRF
            const stateParam = generateCodeVerifier(32);

            // Store the current URL to redirect back after authentication
            const returnUrl = location.pathname + location.search;
            storeOAuthState({ returnUrl, workspaceId }, stateParam);

            // Store the code verifier for later use
            storeCodeVerifier(codeVerifier, stateParam);

            // Store the client ID and issuer for the callback page
            sessionStorage.setItem(`oauth_client_id_${stateParam}`, clientId);
            sessionStorage.setItem(`oauth_issuer_${stateParam}`, issuer);

            // Build the authorization URL
            const authUrl = new URL(authorizeUrl);
            authUrl.searchParams.append('client_id', clientId);
            authUrl.searchParams.append('redirect_uri', redirectUrl);

            // Add common parameters for all providers
            authUrl.searchParams.append('scope', scope);
            authUrl.searchParams.append('state', stateParam);

            // GitHub supports PKCE as per their documentation
            authUrl.searchParams.append('response_type', 'code');
            authUrl.searchParams.append('code_challenge', codeChallenge);
            authUrl.searchParams.append('code_challenge_method', 'S256');

            // Redirect to the authorization URL
            window.location.href = authUrl.toString();
        } catch (error) {
            console.error('OAuth login error:', error);
            setIsLoading(false);
        }
    };

    return (
        <Button
            onClick={handleLogin}
            disabled={isLoading}
            className={className}
        >
            {isLoading ? (
                <>
                    <span className="mr-2">Loading...</span>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                    </svg>
                </>
            ) : (
                label
            )}
        </Button>
    );
};



export const OAuth2Callback: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        const handleCallback = async () => {
            try {
                // Parse URL parameters
                const params = parseUrlParams(location.search);

                // Check for error in the callback
                if (params.error) {
                    setStatus('error');
                    setErrorMessage(`Authentication error: ${params.error_description || params.error}`);
                    return;
                }

                // Get required parameters
                const { code, state } = params;
                if (!code || !state) {
                    setStatus('error');
                    setErrorMessage('Missing required parameters');
                    return;
                }

                // Retrieve stored state and code verifier
                const storedState = getOAuthState(state);
                const codeVerifier = getCodeVerifier(state);

                if (!storedState || !codeVerifier) {
                    setStatus('error');
                    setErrorMessage('Invalid state or session expired');
                    return;
                }

                // Extract provider and redirect URL from the state
                const providerMatch = location.pathname.match(/\/oauth2\/callback\/([^/]+)/);
                const provider = providerMatch ? providerMatch[1] : '';

                if (!provider) {
                    setStatus('error');
                    setErrorMessage('Invalid callback URL');
                    return;
                }

                // Determine token endpoint based on provider
                let tokenUrl = '';
                if (provider === 'github') {
                    tokenUrl = 'https://github.com/login/oauth/access_token';
                } else if (provider === 'gitlab') {
                    tokenUrl = 'https://gitlab.com/oauth/token';
                } else {
                    setStatus('error');
                    setErrorMessage(`Unsupported provider: ${provider}`);
                    return;
                }

                // Extract redirect URL from the current location
                const redirectUri = `${window.location.origin}/oauth2/callback/${provider}`;

                // Get client ID from URL or session storage
                const clientId = params.client_id || sessionStorage.getItem(`oauth_client_id_${state}`);
                if (!clientId) {
                    setStatus('error');
                    setErrorMessage('Missing client ID');
                    return;
                }

                // Exchange code for token
                let tokenResponse;

                if (provider === 'github') {
                    // GitHub requires client_secret which should be kept on server side
                    // For this client-side only implementation, we'll use the approach with code_verifier
                    tokenResponse = await fetch(tokenUrl, {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json'
                        },
                        body: new URLSearchParams({
                            client_id: clientId,
                            code,
                            redirect_uri: redirectUri,
                            code_verifier: codeVerifier // GitHub does support PKCE
                        })
                    });
                } else if (provider === 'gitlab') {
                    // Standard OAuth2 PKCE flow for other providers
                    tokenResponse = await fetch(tokenUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        },
                        body: JSON.stringify({
                            client_id: clientId,
                            code,
                            code_verifier: codeVerifier,
                            redirect_uri: redirectUri,
                            grant_type: 'authorization_code'
                        })
                    });
                } else {
                    setStatus('error');
                    setErrorMessage(`Unexpected OAuth2 provider: ${provider}`);
                    return;
                }

                if (!tokenResponse.ok) {
                    const errorData = await tokenResponse.json();
                    setStatus('error');
                    setErrorMessage(`Token exchange failed: ${errorData.error_description || errorData.error || 'Unknown error'}`);
                    return;
                }

                const tokenData = await tokenResponse.json();

                // Extract workspace ID from state or use default
                const workspaceId = storedState.workspaceId;

                // Extract user info from token if possible
                let username = '';
                let issuer = '';
                try {
                    // For JWT tokens, try to decode the payload
                    if (tokenData.id_token) {
                        // console.log("ID Token", tokenData.id_token);
                        const decodedToken = jwtDecode(tokenData.id_token);

                        // @ts-ignore the field is indeed there, in practice...
                        username = decodedToken.preferred_username || decodedToken.email || decodedToken.sub;
                        if (decodedToken.iss) {
                            issuer = decodedToken.iss;
                        }
                    }

                    // If we couldn't get username from id_token, try to get it from userinfo endpoint
                    if (!username && tokenData.access_token) {
                        // This would be an async call to the userinfo endpoint
                        // For now, we'll use a placeholder
                        username = 'user';
                    }
                } catch (error) {
                    console.error('Error extracting user info from token:', error);
                    username = 'unknown';
                }

                // Create credential ID with provider and username
                const credentialId = `${provider}[${username}]`;

                // Store the token in IndexedDB
                const credentialValue = JSON.stringify({
                    type: 'bearer_token',
                    provider,
                    issuer,
                    id_token: tokenData.id_token,
                    access_token: tokenData.access_token,
                    refresh_token: tokenData.refresh_token
                } as CredentialValue);

                await saveCredential(workspaceId, credentialId, credentialValue);

                console.log(`OAuth token stored for workspace: ${workspaceId}`);

                // Clean up session storage
                clearOAuthState(state);
                clearCodeVerifier(state);

                setStatus('success');

                // Redirect back to the original page after a short delay
                setTimeout(() => {
                    navigate(storedState.returnUrl || '/');
                }, 1500);
            } catch (error) {
                console.error('OAuth callback error:', error);
                setStatus('error');
                setErrorMessage(error instanceof Error ? error.message : 'Unknown error');
            }
        };

        handleCallback();
    }, [location]);

    return (
        <div className="flex items-center justify-center min-h-screen bg-cm-bg-elevated">
            <Card className="w-full max-w-md">
                <div className="flex flex-col items-center p-6 text-center">
                    {status === 'loading' && (
                        <>
                            <Spinner size="xl" />
                            <h5 className="mt-4 text-xl font-bold tracking-tight text-cm-text-primary">
                                Processing authentication...
                            </h5>
                            <p className="mt-2 text-cm-text-primary">
                                Please wait while we complete the authentication process.
                            </p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900">
                                <svg className="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h5 className="mt-4 text-xl font-bold tracking-tight text-cm-text-primary">
                                Authentication successful!
                            </h5>
                            <p className="mt-2 text-cm-text-primary">
                                You will be redirected back to the application in a moment.
                            </p>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <Alert color="failure" className="w-full">
                                <h5 className="text-lg font-medium">Authentication failed</h5>
                                <p>{errorMessage || 'An unknown error occurred'}</p>
                            </Alert>
                            <button
                                onClick={() => navigate('/')}
                                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Return to home
                            </button>
                        </>
                    )}
                </div>
            </Card>
        </div>
    );
};
